import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageWrapper, PageContainer } from '../../components/common/PageWrapper.styles';
import { AdminContent } from './AdminPage.styles';
import AdminTabs, { TabConfig } from '../../components/admin/AdminTabs';
import {
    CoatingTab,
    CoatingNoteTab,
    CoatingManufacturerTab,
    DraegerTubeTab,
    SafetyHazardTab,
    CleaningTab,
    ChartererTab,
    OperationalTab,
    WallwashTab,
    EmergencyTab,
} from '../../components/admin';

import useGetCoatings from '../../api/useGetCoatings';
import useGetCoatingNotes from '../../api/useGetCoatingNotes';
import useGetCoatingManufacturers from '../../api/useGetCoatingManufacturers';
import useGetDraegerTubes from '../../api/useGetDraegerTubes';
import useGetSafetyHazards from '../../api/useGetSafetyHazards';
import useGetProcedures from '../../api/useGetProcedures';
import { PROCEDURE_CATEGORIES } from '../../types/ProcedureTypes';
import { useAppSettings } from '../../hooks/useAppSettings';
import { useAuthStore } from '../../context/AuthContext';

interface AdminPageProps {}

// Tab configuration
const ADMIN_TABS: TabConfig[] = [
    { id: 'coating', label: 'Coating' },
    { id: 'coating-notes', label: 'Coating Notes' },
    { id: 'coating-manufacturers', label: 'Coating Manufacturers' },
    { id: 'draeger', label: 'Draeger tubes' },
    { id: 'safety', label: 'Safety Hazards' },
    { id: 'cleaning', label: 'Cleaning', rightAlign: true },
    { id: 'charterer', label: 'Charterer' },
    { id: 'operational', label: 'Operational' },
    { id: 'wallwash', label: 'Wallwash' },
    { id: 'emergency', label: 'Emergency' },
];

const AdminPage: React.FC<AdminPageProps> = () => {
    const { settings: appSettings, isLoading: appSettingsLoading } = useAppSettings();
    const { token } = useAuthStore();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get initial tab from URL or default to 'coating'
    const tabFromUrl = searchParams.get('tab');
    const initialTab = ADMIN_TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl! : 'coating';

    const [activeTab, setActiveTabState] = useState(initialTab);

    // Handle tab change - update both state and URL
    const handleTabChange = useCallback(
        (tabId: string) => {
            setActiveTabState(tabId);
            setSearchParams({ tab: tabId }, { replace: true });
        },
        [setSearchParams]
    );

    const [selectedCoating, setSelectedCoating] = useState<any>(null);
    const [selectedCoatingNote, setSelectedCoatingNote] = useState<any>(null);
    const [selectedCoatingManufacturer, setSelectedCoatingManufacturer] = useState<any>(null);
    const [selectedDraegerTube, setSelectedDraegerTube] = useState<any>(null);
    const [selectedSafetyHazard, setSelectedSafetyHazard] = useState<any>(null);
    const [selectedCleaningProcedure, setSelectedCleaningProcedure] = useState<any>(null);
    const [selectedChartererProcedure, setSelectedChartererProcedure] = useState<any>(null);
    const [selectedOperationalProcedure, setSelectedOperationalProcedure] = useState<any>(null);
    const [selectedWallwashProcedure, setSelectedWallwashProcedure] = useState<any>(null);
    const [selectedEmergencyProcedure, setSelectedEmergencyProcedure] = useState<any>(null);
    const { coatings, loading: coatingsLoading, error: coatingsError, getCoatings } = useGetCoatings();
    const { coatingNotes, loading: coatingNotesLoading, error: coatingNotesError, getCoatingNotes } = useGetCoatingNotes();
    const {
        manufacturers: coatingManufacturers,
        loading: coatingManufacturersLoading,
        error: coatingManufacturersError,
        getCoatingManufacturers,
    } = useGetCoatingManufacturers();
    const { draegerTubes, loading: draegerLoading, error: draegerError, getDraegerTubes } = useGetDraegerTubes();
    const { safetyHazards, loading: safetyLoading, error: safetyError, getSafetyHazards } = useGetSafetyHazards();
    const {
        procedures: cleaningProcedures,
        loading: cleaningLoading,
        error: cleaningError,
        getProcedures: getCleaningProcedures,
    } = useGetProcedures(PROCEDURE_CATEGORIES.CLEANING);
    const {
        procedures: chartererProcedures,
        loading: chartererLoading,
        error: chartererError,
        getProcedures: getChartererProcedures,
    } = useGetProcedures(PROCEDURE_CATEGORIES.CHARTERER);
    const {
        procedures: operationalProcedures,
        loading: operationalLoading,
        error: operationalError,
        getProcedures: getOperationalProcedures,
    } = useGetProcedures(PROCEDURE_CATEGORIES.OPERATIONAL);
    const {
        procedures: wallwashProcedures,
        loading: wallwashLoading,
        error: wallwashError,
        getProcedures: getWallwashProcedures,
    } = useGetProcedures(PROCEDURE_CATEGORIES.WALLWASH);
    const {
        procedures: emergencyProcedures,
        loading: emergencyLoading,
        error: emergencyError,
        getProcedures: getEmergencyProcedures,
    } = useGetProcedures(PROCEDURE_CATEGORIES.EMERGENCY);

    useEffect(() => {
        // Wait for app settings and token to be available before making API calls
        if (appSettingsLoading || !appSettings?.api || !token) {
            console.log('[AdminPage] Waiting for settings and token...', {
                appSettingsLoading,
                hasApi: !!appSettings?.api,
                hasToken: !!token,
            });
            return;
        }

        console.log('[AdminPage] Loading data for tab:', activeTab);

        // Load data when component mounts or when specific tab is selected
        if (activeTab === 'coating') {
            getCoatings();
        } else if (activeTab === 'coating-notes') {
            getCoatingNotes();
        } else if (activeTab === 'coating-manufacturers') {
            getCoatingManufacturers();
        } else if (activeTab === 'draeger') {
            getDraegerTubes();
        } else if (activeTab === 'safety') {
            getSafetyHazards();
        } else if (activeTab === 'cleaning') {
            getCleaningProcedures();
        } else if (activeTab === 'charterer') {
            getChartererProcedures();
        } else if (activeTab === 'operational') {
            getOperationalProcedures();
        } else if (activeTab === 'wallwash') {
            getWallwashProcedures();
        } else if (activeTab === 'emergency') {
            getEmergencyProcedures();
        }
    }, [
        activeTab,
        appSettingsLoading,
        appSettings?.api,
        token,
        getCoatings,
        getCoatingNotes,
        getCoatingManufacturers,
        getDraegerTubes,
        getSafetyHazards,
        getCleaningProcedures,
        getChartererProcedures,
        getOperationalProcedures,
        getWallwashProcedures,
        getEmergencyProcedures,
    ]);

    // Auto-select first item when data loads
    useEffect(() => {
        if (coatings.length > 0 && !selectedCoating) {
            setSelectedCoating(coatings[0]);
        }
    }, [coatings, selectedCoating]);

    useEffect(() => {
        if (coatingNotes.length > 0 && !selectedCoatingNote) {
            setSelectedCoatingNote(coatingNotes[0]);
        }
    }, [coatingNotes, selectedCoatingNote]);

    useEffect(() => {
        if (coatingManufacturers.length > 0 && !selectedCoatingManufacturer) {
            setSelectedCoatingManufacturer(coatingManufacturers[0]);
        }
    }, [coatingManufacturers, selectedCoatingManufacturer]);

    useEffect(() => {
        if (draegerTubes.length > 0 && !selectedDraegerTube) {
            setSelectedDraegerTube(draegerTubes[0]);
        }
    }, [draegerTubes, selectedDraegerTube]);

    useEffect(() => {
        if (safetyHazards.length > 0 && !selectedSafetyHazard) {
            setSelectedSafetyHazard(safetyHazards[0]);
        }
    }, [safetyHazards, selectedSafetyHazard]);

    useEffect(() => {
        if (cleaningProcedures.length > 0 && !selectedCleaningProcedure) {
            setSelectedCleaningProcedure(cleaningProcedures[0]);
        }
    }, [cleaningProcedures, selectedCleaningProcedure]);

    useEffect(() => {
        if (chartererProcedures.length > 0 && !selectedChartererProcedure) {
            setSelectedChartererProcedure(chartererProcedures[0]);
        }
    }, [chartererProcedures, selectedChartererProcedure]);

    useEffect(() => {
        if (operationalProcedures.length > 0 && !selectedOperationalProcedure) {
            setSelectedOperationalProcedure(operationalProcedures[0]);
        }
    }, [operationalProcedures, selectedOperationalProcedure]);

    useEffect(() => {
        if (wallwashProcedures.length > 0 && !selectedWallwashProcedure) {
            setSelectedWallwashProcedure(wallwashProcedures[0]);
        }
    }, [wallwashProcedures, selectedWallwashProcedure]);

    useEffect(() => {
        if (emergencyProcedures.length > 0 && !selectedEmergencyProcedure) {
            setSelectedEmergencyProcedure(emergencyProcedures[0]);
        }
    }, [emergencyProcedures, selectedEmergencyProcedure]);
    return (
        <PageWrapper>
            <PageContainer>
                <AdminContent>
                    <AdminTabs tabs={ADMIN_TABS} activeTab={activeTab} onTabChange={handleTabChange} />
                    {/* Coating Tab */}
                    {activeTab === 'coating' && (
                        <CoatingTab
                            coatings={coatings}
                            coatingsLoading={coatingsLoading}
                            coatingsError={coatingsError}
                            selectedCoating={selectedCoating}
                            setSelectedCoating={setSelectedCoating}
                            onRefresh={getCoatings}
                        />
                    )}

                    {/* Coating Notes Tab */}
                    {activeTab === 'coating-notes' && (
                        <CoatingNoteTab
                            coatingNotes={coatingNotes}
                            loading={coatingNotesLoading}
                            error={coatingNotesError}
                            selectedCoatingNote={selectedCoatingNote}
                            setSelectedCoatingNote={setSelectedCoatingNote}
                            onRefresh={getCoatingNotes}
                        />
                    )}

                    {/* Coating Manufacturers Tab */}
                    {activeTab === 'coating-manufacturers' && (
                        <CoatingManufacturerTab
                            manufacturers={coatingManufacturers}
                            loading={coatingManufacturersLoading}
                            error={coatingManufacturersError}
                            selectedManufacturer={selectedCoatingManufacturer}
                            setSelectedManufacturer={setSelectedCoatingManufacturer}
                            coatingNotes={coatingNotes}
                            onNavigateToNote={(note) => {
                                setSelectedCoatingNote(note);
                                handleTabChange('coating-notes');
                            }}
                            onRefresh={getCoatingManufacturers}
                        />
                    )}

                    {/* Draeger Tubes Tab */}
                    {activeTab === 'draeger' && (
                        <DraegerTubeTab
                            draegerTubes={draegerTubes}
                            draegerLoading={draegerLoading}
                            draegerError={draegerError}
                            selectedDraegerTube={selectedDraegerTube}
                            setSelectedDraegerTube={setSelectedDraegerTube}
                            onRefresh={getDraegerTubes}
                        />
                    )}

                    {/* Safety Hazards Tab */}
                    {activeTab === 'safety' && (
                        <SafetyHazardTab
                            safetyHazards={safetyHazards}
                            safetyLoading={safetyLoading}
                            safetyError={safetyError}
                            selectedSafetyHazard={selectedSafetyHazard}
                            setSelectedSafetyHazard={setSelectedSafetyHazard}
                            onRefresh={getSafetyHazards}
                        />
                    )}

                    {/* Cleaning Tab */}
                    {activeTab === 'cleaning' && (
                        <CleaningTab
                            procedures={cleaningProcedures}
                            loading={cleaningLoading}
                            error={cleaningError}
                            selectedProcedure={selectedCleaningProcedure}
                            setSelectedProcedure={setSelectedCleaningProcedure}
                            onRefresh={getCleaningProcedures}
                        />
                    )}

                    {/* Charterer Tab */}
                    {activeTab === 'charterer' && (
                        <ChartererTab
                            procedures={chartererProcedures}
                            loading={chartererLoading}
                            error={chartererError}
                            selectedProcedure={selectedChartererProcedure}
                            setSelectedProcedure={setSelectedChartererProcedure}
                            onRefresh={getChartererProcedures}
                        />
                    )}

                    {/* Operational Tab */}
                    {activeTab === 'operational' && (
                        <OperationalTab
                            procedures={operationalProcedures}
                            loading={operationalLoading}
                            error={operationalError}
                            selectedProcedure={selectedOperationalProcedure}
                            setSelectedProcedure={setSelectedOperationalProcedure}
                            onRefresh={getOperationalProcedures}
                        />
                    )}

                    {/* Wallwash Tab */}
                    {activeTab === 'wallwash' && (
                        <WallwashTab
                            procedures={wallwashProcedures}
                            loading={wallwashLoading}
                            error={wallwashError}
                            selectedProcedure={selectedWallwashProcedure}
                            setSelectedProcedure={setSelectedWallwashProcedure}
                            onRefresh={getWallwashProcedures}
                        />
                    )}

                    {/* Emergency Tab */}
                    {activeTab === 'emergency' && (
                        <EmergencyTab
                            procedures={emergencyProcedures}
                            loading={emergencyLoading}
                            error={emergencyError}
                            selectedProcedure={selectedEmergencyProcedure}
                            setSelectedProcedure={setSelectedEmergencyProcedure}
                            onRefresh={getEmergencyProcedures}
                        />
                    )}
                </AdminContent>
            </PageContainer>
        </PageWrapper>
    );
};

export default AdminPage;
